/**
 * ReportHeader.jsx — Top-of-Analysis report context strip.
 *
 * Replaces the compact metadata strip with a richer, scannable header showing:
 *   - simulation name
 *   - population
 *   - stimulus preview
 *   - responses / actual N
 *   - coverage badge
 *   - mode (uncertainty-aware / sampled)
 *   - context badge
 *   - reliability badge (compact ReliabilitySummary)
 *   - completed date
 *   - export buttons (slot)
 *
 * Renders interpretation cues first; export buttons are visually secondary.
 */
import { Badge } from '../ui/badge';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ContextInfluenceBadge } from './ContextInfluenceBadge';
import ReliabilitySummary from './ReliabilitySummary';
import { formatDate } from '../../lib/formatters';

export default function ReportHeader({
  simulation,
  population = null,
  stimulus = null,
  preData = null,
  className,
  exportSlot = null,
  testId = 'report-header',
}) {
  if (!simulation) return null;

  const config = simulation.config || {};
  const contextMode = config.context_mode || simulation.context_mode || 'standard';
  const totalN = simulation.population_total_n;
  const actualN = simulation.actual_n ?? simulation.total_responses ?? simulation.sample_size;
  const coverageRate = simulation.coverage_rate;
  const isFull = !!simulation.is_full_census;
  const incomplete = !!simulation.incomplete_census_flag;
  const completedAt = simulation.completed_at || simulation.created_at;

  // Status label (replaces "Uncertainty-aware" technical tag with simple status)
  const status = simulation.status || 'completed';
  const statusLabel =
    status === 'completed_with_warning' ? 'Completed (with warnings)' :
    status === 'failed' ? 'Failed' :
    status === 'running' ? 'Running' :
    status === 'queued' ? 'Queued' :
    'Completed';
  const statusTone =
    status === 'completed_with_warning' ? 'bg-warning-soft text-warning-soft-foreground border-warning-border' :
    status === 'failed' ? 'bg-negative-soft text-negative-soft-foreground border-negative-border' :
    status === 'running' || status === 'queued' ? 'bg-blue-50 text-blue-700 border-blue-200' :
    'bg-positive-soft text-positive-soft-foreground border-positive-border';

  const stimulusText = stimulus?.prompt_text || '';
  const stimulusPreview = stimulusText.length > 90 ? `${stimulusText.slice(0, 90)}…` : stimulusText;

  // Coverage display
  let coverageLabel = null;
  if (actualN != null && totalN != null) {
    const cov = coverageRate != null ? (coverageRate * 100).toFixed(0) : ((actualN / Math.max(totalN, 1)) * 100).toFixed(0);
    coverageLabel = `${actualN}/${totalN} (${cov}%)`;
  } else if (actualN != null) {
    coverageLabel = `${actualN} responses`;
  }

  const coverageTone = incomplete
    ? 'bg-warning-soft text-warning-soft-foreground border-warning-border'
    : isFull
    ? 'bg-positive-soft text-positive-soft-foreground border-positive-border'
    : 'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <div
      className={cn(
        'bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl p-4 shadow-sm',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Left: identity + interpretation */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h2
              className="text-lg font-bold text-slate-900 dark:text-white tracking-tight truncate"
              data-testid={`${testId}-name`}
            >
              {simulation.name}
            </h2>
            <Badge
              variant="outline"
              className={cn('text-[10px] font-medium', statusTone)}
              title={`Simulation status: ${statusLabel}`}
              data-testid={`${testId}-status-badge`}
            >
              {statusLabel}
            </Badge>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
            {population?.name && (
              <span data-testid={`${testId}-population`}>
                Population: <span className="text-slate-700 dark:text-slate-200 font-medium">{population.name}</span>
              </span>
            )}
            {stimulusPreview && (
              <>
                <span className="text-slate-300">·</span>
                <span className="truncate max-w-md" title={stimulusText} data-testid={`${testId}-stimulus`}>
                  &ldquo;{stimulusPreview}&rdquo;
                </span>
              </>
            )}
          </div>

          {/* Badges: coverage · context · reliability */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {coverageLabel && (
              <Badge
                variant="outline"
                className={cn('border px-2 py-0.5 text-[11px] font-medium', coverageTone)}
                title={
                  incomplete
                    ? 'Intended full population but processed fewer individuals.'
                    : isFull
                    ? 'Full census processed.'
                    : 'Sample of population.'
                }
                data-testid={`${testId}-coverage`}
              >
                Coverage: {coverageLabel}
                {isFull && !incomplete && ' · full'}
                {incomplete && ' · incomplete'}
              </Badge>
            )}
            <ContextInfluenceBadge
              mode={contextMode}
              maxChars={config.context_max_chars}
              testId={`${testId}-context`}
            />
            <ReliabilitySummary
              mode="compact"
              simulation={simulation}
              preData={preData}
              testId={`${testId}-reliability`}
            />
            {completedAt && (
              <Badge
                variant="outline"
                className="text-[11px] font-normal text-slate-600 bg-slate-50 border-slate-200 gap-1"
                data-testid={`${testId}-completed`}
              >
                <Calendar className="w-3 h-3" />
                {formatDate(completedAt)}
              </Badge>
            )}
          </div>
        </div>

        {/* Right: export buttons (visually secondary) */}
        {exportSlot && (
          <div className="flex items-center gap-1.5 flex-shrink-0" data-testid={`${testId}-exports`}>
            {exportSlot}
          </div>
        )}
      </div>
    </div>
  );
}
